import type { VesselCardZnChartProps } from "../models/vessel-card-zn.models";
import { getVesselCardExpectedCurve } from "../utils/vessel-card-chart.utils";
import { getVesselCardZnAxis, getVesselCardZnPoints } from "../utils/vessel-card-zn.utils";

const VesselCardZnChart = ({ condition, rows, source = "mock" }: VesselCardZnChartProps) => {
  const conditionRows = rows.filter((row) => row.mode.startsWith(condition));
  // ZN predictions never set the DVP curve bounds or replace its graph-only auxiliary allowance.
  const curve = getVesselCardExpectedCurve(
    conditionRows.map((row) => ({
      mode: row.mode,
      expectedSpeed: row.expectedSpeed,
      expectedConsumption: row.expectedConsumption,
      comparisonSpeed: null,
      comparisonConsumption: null,
    })),
  );
  const expected = curve.endpoints;
  const model = getVesselCardZnPoints(conditionRows, "model");
  const allPoints = [...curve.points, ...expected, ...model];
  const xAxis = allPoints.length
    ? getVesselCardZnAxis(
        allPoints.map((point) => point.speed),
        0.3,
      )
    : null;
  const yAxis = allPoints.length
    ? getVesselCardZnAxis(
        allPoints.map((point) => point.consumption),
        1,
      )
    : null;
  const x = (speed: number) => 58 + ((speed - xAxis!.start) / (xAxis!.end - xAxis!.start)) * 372;
  const y = (consumption: number) => 230 - ((consumption - yAxis!.start) / (yAxis!.end - yAxis!.start)) * 180;
  const series = [
    {
      name: "Expected Performance",
      key: "expected",
      endpoints: expected,
      points: curve.points,
      className: "text-teal-600 dark:text-teal-300",
    },
    {
      name:
        source === "mock" ? "ZN Fuel Model (mock)" : source === "snowflake" ? "Stored ZN Fuel Model" : "ZN Fuel Model",
      key: "model",
      endpoints: model,
      points: model,
      className: "text-pink-600 dark:text-pink-400",
    },
  ];

  return (
    <article
      className="min-w-0 rounded-md bg-gray-50 p-2 dark:bg-gray-900/50"
      data-test={`vessel-card-zn-${condition.toLowerCase()}-chart`}
    >
      <h4 className="text-center text-sm font-semibold">{condition}</h4>
      <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs">
        {series.map((item) => (
          <span key={item.key} className={item.className}>
            ● {item.name}
          </span>
        ))}
      </div>
      {xAxis && yAxis ? (
        <svg
          viewBox="0 0 460 285"
          className="w-full text-gray-500 dark:text-gray-400"
          role="img"
          aria-label={`${condition}: Expected Performance versus ${source === "mock" ? "mock " : ""}ZN fuel consumption`}
        >
          <desc>
            Speed in knots and consumption in tonnes per day. Expected curves use the workbook's 2.8 power law, 2 t/day
            auxiliary allowance and explicit endpoint values. ZN predictions use{" "}
            {source === "mock"
              ? "synthetic data"
              : source === "snowflake"
                ? "stored main-engine and normal auxiliary consumption"
                : "the default model and normal auxiliary consumption"}. Missing
            values are omitted.
          </desc>
          {xAxis.ticks.map((tick, index) => (
            <g key={`x-${index}`}>
              <line
                x1={x(tick)}
                x2={x(tick)}
                y1={50}
                y2={230}
                stroke="currentColor"
                opacity={0.3}
                strokeDasharray="2 4"
              />
              <text x={x(tick)} y={247} textAnchor="middle" fill="currentColor" fontSize={10}>
                {tick.toFixed(1)}
              </text>
            </g>
          ))}
          {yAxis.ticks.map((tick, index) => (
            <g key={`y-${index}`}>
              <line
                x1={58}
                x2={430}
                y1={y(tick)}
                y2={y(tick)}
                stroke="currentColor"
                opacity={0.3}
                strokeDasharray="2 4"
              />
              <text x={48} y={y(tick) + 3} textAnchor="end" fill="currentColor" fontSize={10}>
                {tick.toFixed(1)}
              </text>
            </g>
          ))}
          <text x={245} y={274} textAnchor="middle" fill="currentColor" fontSize={11}>
            Speed (kn)
          </text>
          <text transform="translate(14 140) rotate(-90)" textAnchor="middle" fill="currentColor" fontSize={11}>
            Consumption (t/day)
          </text>
          {series.map((item) => (
            <g
              key={item.key}
              className={item.className}
              data-test={`vessel-card-zn-${condition.toLowerCase()}-${item.key}-series`}
            >
              <polyline
                points={item.points.map((point) => `${x(point.speed)},${y(point.consumption)}`).join(" ")}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeDasharray={item.key === "model" ? "6 4" : undefined}
              />
              {item.endpoints.map((point, index) => (
                <g key={index}>
                  <circle cx={x(point.speed)} cy={y(point.consumption)} r={3} fill="currentColor">
                    <title>{`${item.name}: ${point.speed.toFixed(1)} kn / ${point.consumption.toFixed(1)} t/day`}</title>
                  </circle>
                  <text
                    x={x(point.speed)}
                    y={y(point.consumption) + (item.key === "expected" ? -10 : 17)}
                    textAnchor={index === 0 ? "start" : "end"}
                    fill="currentColor"
                    fontSize={10}
                  >
                    {point.speed.toFixed(1)} / {point.consumption.toFixed(1)}
                  </text>
                </g>
              ))}
            </g>
          ))}
        </svg>
      ) : (
        <p className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">No speed/consumption data available.</p>
      )}
      {expected.length > 0 && !curve.available && (
        <p role="status" className="text-center text-xs text-gray-500 dark:text-gray-400">
          Expected curve unavailable for {condition}; showing available expected points.
        </p>
      )}
    </article>
  );
};

export default VesselCardZnChart;
